import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Check, ChevronDown, PlusCircle, Sparkles } from 'lucide-react';
import { tg } from '../../telegram';
import { formatQty } from '../../utils/format';
import { toComparisonKey } from '../../utils/textNormalizer';

export interface DropdownItemOption {
  name: string;
  category?: string;
  unit?: string;
  type?: 'RAW' | 'PREPARED';
  displayName?: string;
  package_unit?: string;
  ratio?: number;
  key?: string;
}

interface ItemDropdownProps {
  value: string;
  selectedUnit?: string;
  onChange: (name: string, defaultUnit?: string, option?: DropdownItemOption) => void;
  items: DropdownItemOption[];
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
  showCategoryGroups?: boolean;
  disabled?: boolean;
  displayMode?: 'name_only' | 'name_with_unit' | 'display_name' | 'auto';
}

// Convert Vietnamese string to lowercase unaccented text for fuzzy search
function removeVietnameseTones(str: string): string {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

export const ItemDropdown: React.FC<ItemDropdownProps> = ({
  value,
  selectedUnit,
  onChange,
  items,
  placeholder = '-- Chọn nguyên liệu / món --',
  style,
  className = 'input',
  showCategoryGroups = true,
  disabled = false,
  displayMode = 'auto',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find currently selected item
  const selectedItem = useMemo(() => {
    if (!value) return null;
    const lowerVal = value.trim().toLowerCase();
    const canonVal = toComparisonKey(value);
    const cleanUnit = (selectedUnit || '').trim().toLowerCase();

    // 1. If selectedUnit is provided, prefer the item matching both name and unit
    if (cleanUnit) {
      const matchWithUnit = items.find(
        (it) =>
          (it.name.toLowerCase() === lowerVal || toComparisonKey(it.name) === canonVal) &&
          ((it.package_unit && it.package_unit.trim().toLowerCase() === cleanUnit) ||
            (it.unit && it.unit.trim().toLowerCase() === cleanUnit))
      );
      if (matchWithUnit) return matchWithUnit;
    }

    // 2. Fallback to exact key or name match
    return (
      items.find(
        (it) =>
          it.key === value ||
          it.name.toLowerCase() === lowerVal ||
          (it.displayName && it.displayName.toLowerCase() === lowerVal)
      ) ||
      items.find(
        (it) =>
          toComparisonKey(it.name) === canonVal ||
          (it.displayName && toComparisonKey(it.displayName) === canonVal) ||
          (it.key && toComparisonKey(it.key) === canonVal)
      )
    );
  }, [value, selectedUnit, items]);

  // Display label for trigger button
  const displayLabel = useMemo(() => {
    if (displayMode === 'name_with_unit' && (value || selectedItem)) {
      const name = selectedItem ? selectedItem.name : value;
      const unit = selectedUnit || selectedItem?.package_unit || selectedItem?.unit;
      return unit ? `${name} (${unit})` : name;
    }
    if (displayMode === 'name_only' && (value || selectedItem)) {
      return selectedItem ? selectedItem.name : value;
    }
    if (selectedItem) {
      return selectedItem.displayName || selectedItem.name;
    }
    return value || placeholder;
  }, [selectedItem, value, placeholder, displayMode, selectedUnit]);

  const isCustomValue = Boolean(value && !selectedItem);

  // Extract unique categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    items.forEach((it) => {
      if (it.category) set.add(it.category);
      else set.add('Khác');
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [items]);

  // Filter items by search query and category
  const filteredItems = useMemo(() => {
    const rawQuery = searchQuery.trim();
    const cleanQuery = removeVietnameseTones(rawQuery);
    const queryParts = cleanQuery.split(/\s+/).filter(Boolean);

    return items.filter((it) => {
      // Category filter
      if (selectedCategory !== 'ALL') {
        const itemCat = it.category || 'Khác';
        if (itemCat !== selectedCategory) return false;
      }

      if (!queryParts.length) return true;

      const rawTarget = `${it.name} ${it.displayName || ''} ${it.category || ''} ${it.package_unit || ''} ${it.unit || ''}`;
      const cleanTarget = removeVietnameseTones(rawTarget);

      // Every word in search query must appear in cleanTarget
      return queryParts.every((part) => cleanTarget.includes(part));
    });
  }, [items, searchQuery, selectedCategory]);

  // Group filtered items by category
  const groupedItems = useMemo(() => {
    if (!showCategoryGroups) {
      return [{ category: '', items: filteredItems }];
    }

    const map = new Map<string, DropdownItemOption[]>();
    for (const it of filteredItems) {
      const cat = it.category || 'Khác';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(it);
    }

    return Array.from(map.entries())
      .map(([cat, list]) => ({
        category: cat,
        items: list,
      }))
      .sort((a, b) => a.category.localeCompare(b.category, 'vi'));
  }, [filteredItems, showCategoryGroups]);

  // Open modal & focus search input
  const handleOpen = () => {
    if (disabled) return;
    tg.haptic.impact('light');
    setSearchQuery('');
    setSelectedCategory('ALL');
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleSelectItem = (it: DropdownItemOption) => {
    tg.haptic.selectionChanged();
    onChange(it.name, it.package_unit || it.unit, it);
    handleClose();
  };

  const handleSelectCustom = (customName: string) => {
    const trimmed = customName.trim();
    if (!trimmed) return;
    tg.haptic.selectionChanged();
    onChange(trimmed);
    handleClose();
  };

  return (
    <>
      {/* Clickable Trigger Box */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={`custom-dropdown-trigger ${className || ''}`}
        title={displayLabel}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          width: '100%',
          minHeight: 38,
          height: 38,
          padding: '6px 10px',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: 'var(--bg-surface)',
          color: value ? 'var(--text-primary)' : 'var(--text-tertiary)',
          border: '1.5px solid var(--border-color)',
          borderRadius: 'var(--radius-md)',
          boxSizing: 'border-box',
          ...style,
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: value ? 700 : 500,
            fontSize: 13,
          }}
        >
          {isCustomValue && '⭐ '}
          {displayLabel}
        </span>
        <ChevronDown size={15} style={{ flexShrink: 0, color: 'var(--text-tertiary)' }} />
      </button>

      {/* Searchable Picker Modal / Bottom Sheet */}
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="modal-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              backgroundColor: 'rgba(21, 17, 14, 0.65)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
            onClick={handleClose}
          >
            <div
              className="searchable-picker-sheet"
              style={{
                width: '100%',
                maxWidth: 480,
                maxHeight: '88vh',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: '20px 20px 0 0',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'var(--shadow-lg)',
                overflow: 'hidden',
                animation: 'slideUp 0.2s ease-out',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top Drag Indicator Handle */}
              <div
                style={{
                  width: '100%',
                  padding: '10px 0 4px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'var(--border-color)',
                  }}
                />
              </div>

              {/* Modal Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px 12px',
                  borderBottom: '1px solid var(--border-color)',
                }}
              >
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                    Chọn Nguyên Liệu / Món
                  </h3>
                  <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Có {items.length} món trong danh mục. Gõ từ khóa để lọc nhanh.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="stepper-btn"
                  style={{ width: 32, height: 32, padding: 0, color: 'var(--text-secondary)' }}
                  title="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Search Bar Input (KEY FEATURE FOR INSTANT TEXT SEARCH) */}
              <div style={{ padding: '12px 16px 8px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1.5px solid var(--border-focus)',
                    backgroundColor: 'var(--bg-canvas)',
                    boxShadow: '0 0 0 3px rgba(111, 64, 37, 0.15)',
                  }}
                >
                  <Search size={18} style={{ color: 'var(--primary-accent)', flexShrink: 0 }} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="🔍 Gõ tên tìm kiếm (VD: bot, sua, ca phe...)"
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      padding: 0,
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: 'var(--text-tertiary)',
                        cursor: 'pointer',
                        padding: 2,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      title="Xóa tìm kiếm"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter Chips */}
              {showCategoryGroups && categories.length > 1 && (
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    padding: '4px 16px 10px',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedCategory('ALL')}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 12,
                      fontWeight: 700,
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      backgroundColor:
                        selectedCategory === 'ALL' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: selectedCategory === 'ALL' ? '#FFFFFF' : 'var(--text-secondary)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    Tất cả ({items.length})
                  </button>
                  {categories.map((cat) => {
                    const count = items.filter((it) => (it.category || 'Khác') === cat).length;
                    const isSelected = selectedCategory === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 12,
                          fontWeight: 700,
                          border: 'none',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          backgroundColor: isSelected ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                          color: isSelected ? '#FFFFFF' : 'var(--text-secondary)',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {cat} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Scrollable Items List */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '4px 16px 16px',
                  maxHeight: '52vh',
                  WebkitOverflowScrolling: 'touch',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {/* Custom / One-off Input Button if search query is entered */}
                {searchQuery.trim() && (
                  <button
                    type="button"
                    onClick={() => handleSelectCustom(searchQuery)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      borderRadius: 'var(--radius-md)',
                      border: '1.5px dashed var(--primary-accent)',
                      backgroundColor: 'var(--primary-accent-subtle)',
                      color: 'var(--primary-accent)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <PlusCircle size={18} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        Sử dụng "{searchQuery.trim()}" (Nhập thủ công)
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                        Bấm vào đây nếu muốn dùng tên mới chưa có trong danh mục
                      </div>
                    </div>
                  </button>
                )}

                {/* No items matched message */}
                {filteredItems.length === 0 && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '32px 16px',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>
                      Không tìm thấy nguyên liệu nào khớp với "{searchQuery}"
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Bạn có thể bấm nút <b>Sử dụng "{searchQuery}"</b> ở trên để nhập tay.
                    </div>
                  </div>
                )}

                {/* Render Grouped Items */}
                {groupedItems.map((group, gIdx) => {
                  if (!group.items.length) return null;
                  return (
                    <div key={gIdx} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {group.category && (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            padding: '4px 6px 2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <Sparkles size={12} color="var(--primary-accent)" />
                          <span>{group.category}</span>
                        </div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {group.items.map((it, idx) => {
                          const isSelected =
                            selectedItem?.name.toLowerCase() === it.name.toLowerCase() &&
                            (!it.package_unit || selectedItem?.package_unit === it.package_unit);

                          return (
                            <button
                              key={it.key || `${it.name}-${it.package_unit || ''}-${idx}`}
                              type="button"
                              className="dropdown-option-item"
                              onClick={() => handleSelectItem(it)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                padding: '10px 12px',
                                borderRadius: 'var(--radius-md)',
                                border: isSelected
                                  ? '1.5px solid var(--primary-accent)'
                                  : '1px solid var(--border-color)',
                                backgroundColor: isSelected
                                  ? 'var(--primary-accent-subtle)'
                                  : 'var(--bg-surface-subtle)',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'all 0.1s ease',
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: 14,
                                    fontWeight: isSelected ? 800 : 700,
                                    color: isSelected ? 'var(--primary-accent)' : 'var(--text-primary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {it.type === 'PREPARED' && (
                                    <span style={{ color: 'var(--color-warning)', marginRight: 4 }}>[BTP]</span>
                                  )}
                                  {it.displayName || it.name}
                                </div>
                                {(it.package_unit || it.unit) && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: 'var(--text-secondary)',
                                      marginTop: 2,
                                      display: 'flex',
                                      gap: 8,
                                    }}
                                  >
                                    {it.package_unit && (
                                      <span>
                                        Quy cách: <b>{it.package_unit}</b>
                                      </span>
                                    )}
                                    {it.ratio && it.ratio > 1 && (
                                      <span>
                                        (= {formatQty(it.ratio, it.unit)})
                                      </span>
                                    )}
                                    {!it.package_unit && it.unit && (
                                      <span>
                                        ĐVT: <b>{it.unit}</b>
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {isSelected ? (
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--primary-accent)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#FFFFFF',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Check size={14} />
                                </div>
                              ) : (
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: 'var(--text-tertiary)',
                                    backgroundColor: 'var(--bg-canvas)',
                                    padding: '3px 8px',
                                    borderRadius: 'var(--radius-sm)',
                                    flexShrink: 0,
                                  }}
                                >
                                  {it.package_unit || it.unit || 'Chọn'}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
